import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { FleetRental } from "../components/FleetRental";
import { BillEditDialog } from "../components/BillEditDialog";
import { ExpenseIframeModal } from "../components/ExpenseIframeModal";
import { BillCard } from "../components/document/BillCard";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useExpensePush } from "../hooks/useExpensePush";
import { toast } from "sonner";
import {
  ArrowLeft, RotateCw, ExternalLink, Building2, AlertTriangle, Download, CheckCircle2,
} from "lucide-react";

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isWriter } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payTarget, setPayTarget] = useState(null);
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [integ, setInteg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getDocument(id);
      setDoc(res.data);
    } catch {
      toast.error("Could not load document");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const {
    expenseLink, expenseBill, expenseLoading, rePushTarget: rePushBill, setRePushTarget: setRePushBill,
    openExpense, requestPush, confirmRePush, closeExpense,
  } = useExpensePush(load);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.integrationsStatus().then((r) => setInteg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (doc?.status !== "processing") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [doc, load]);

  const confirmPay = async () => {
    if (!payTarget) return;
    setSaving(true);
    try {
      await api.markPaid(payTarget.id, conf);
      toast.success("Marked as paid");
      setPayTarget(null);
      load();
    } catch {
      toast.error("Could not mark as paid");
    } finally {
      setSaving(false);
    }
  };

  const undoPay = async (bill) => {
    try {
      await api.markUnpaid(bill.id);
      toast.info("Moved back to unpaid");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const logExpenseFromDialog = async () => {
    if (!payTarget) return;
    const bill = payTarget;
    setPayTarget(null);
    await openExpense(bill);
  };

  const toggleReviewed = async (bill) => {
    const next = !bill.review?.reviewed;
    try {
      await api.markBillReviewed(bill.id, next);
      toast.success(next ? "Marked as reviewed" : "Review cleared");
      load();
    } catch {
      toast.error("Could not update review status");
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-xl" /></div>;
  }
  if (!doc) return <div className="text-sm text-muted-foreground">Document not found.</div>;

  const fileUrl = api.documentFileUrl(id);
  const bills = doc.bills || [];

  const reprocess = async () => {
    await api.reprocessDocument(id);
    toast.info("Reprocessing started");
    setDoc({ ...doc, status: "processing" });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight truncate">{doc.filename}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge status={doc.status} />
            <span>
              {doc.source_filename ? `from ${doc.source_filename}` : `${bills.length} bill(s)`}
              {doc.page_start ? ` · pages ${doc.page_start}–${doc.page_end}` : ""}
              {` · uploaded ${formatDate(doc.uploaded_at)}`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => window.open(api.exportCsvUrl(), "_blank")} data-testid="document-detail-export-csv-button"><Download className="h-4 w-4" /> CSV</Button>
          <Button variant="outline" className="gap-2" onClick={() => window.open(api.exportExcelUrl(), "_blank")} data-testid="document-detail-export-excel-button"><Download className="h-4 w-4" /> Excel</Button>
          {isWriter && (
            <Button variant="outline" className="gap-2" onClick={reprocess} data-testid="document-detail-reprocess-button"><RotateCw className="h-4 w-4" /> Reprocess</Button>
          )}
        </div>
      </div>

      {doc.status === "failed" && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <div className="font-medium text-sm text-red-800">We couldn't extract data from this PDF</div>
              <div className="text-sm text-red-700 mt-0.5">{doc.error || "Try reprocessing or upload a clearer scan."}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: data */}
        <div className="space-y-5">
          {doc.status === "processing" && (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Extracting fields… This can take ~10–30 seconds per PDF.</CardContent></Card>
          )}
          {bills.map((b, idx) => (
            <React.Fragment key={b.id}>
              <BillCard
                bill={b}
                idx={idx}
                billsCount={bills.length}
                isWriter={isWriter}
                expenseLoading={expenseLoading}
                handlers={{
                  onEdit: setEditTarget,
                  onToggleReviewed: toggleReviewed,
                  onMarkPaidOpen: (bill) => { setPayTarget(bill); setConf(""); },
                  onUndoPay: undoPay,
                  onRequestPush: requestPush,
                  onNavigate: navigate,
                }}
              />
              <FleetRental bill={b} onUpdated={() => load()} rentflowEnabled={integ ? !integ.rentflow?.is_off : true} rentflowPushEnabled={integ ? !!integ.rentflow?.push?.configured : true} />
            </React.Fragment>
          ))}
          {doc.status === "completed" && bills.length === 0 && (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No bills were extracted from this document.</CardContent></Card>
          )}
        </div>

        {/* Right: PDF preview */}
        <div>
          <Card className="lg:sticky lg:top-20">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold">Original PDF</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => window.open(fileUrl, "_blank")} data-testid="document-pdf-download-button"><ExternalLink className="h-3.5 w-3.5" /> Open</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="document-pdf-viewer">
                <object data={fileUrl} type="application/pdf" className="w-full" style={{ height: "70vh" }}>
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    PDF preview unavailable. <a href={fileUrl} target="_blank" rel="noreferrer" className="text-primary underline">Open in new tab</a>.
                  </div>
                </object>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark paid dialog */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="detail-markpaid-dialog">
          <DialogHeader>
            <DialogTitle>Mark bill as paid</DialogTitle>
            <DialogDescription>
              {payTarget && (
                <span>{payTarget.issuing_authority} · {formatCurrency(payTarget.total_amount_due, payTarget.currency)} · due {formatDate(payTarget.due_date)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="detail-conf">Confirmation number</Label>
            <Input
              id="detail-conf"
              data-testid="detail-confirmation-number-input"
              placeholder="e.g. CONF-123456 (optional)"
              value={conf}
              onChange={(e) => setConf(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmPay()}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">Enter the payment confirmation/receipt number for your records.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)} disabled={saving}>Cancel</Button>
            <Button variant="secondary" onClick={logExpenseFromDialog} disabled={saving || expenseLoading} data-testid="detail-log-expense-button" className="gap-2">
              <Building2 className="h-4 w-4" /> Log Expense
            </Button>
            <Button onClick={confirmPay} disabled={saving} data-testid="detail-confirm-paid-button" className="gap-2">
              <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving..." : "Confirm paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded Expenses "Log expense" iframe */}
      <ExpenseIframeModal
        open={!!expenseLink}
        link={expenseLink}
        bill={expenseBill}
        onClose={closeExpense}
        onLogged={load}
      />

      <BillEditDialog
        bill={editTarget}
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSaved={() => load()}
      />

      <AlertDialog open={!!rePushBill} onOpenChange={(o) => !o && setRePushBill(null)}>
        <AlertDialogContent data-testid="repush-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Push to Expenses again?</AlertDialogTitle>
            <AlertDialogDescription>
              This bill was already pushed to Expenses{rePushBill?.expense_post?.logged_at ? ` on ${formatDate(rePushBill.expense_post.logged_at)}` : ""}. Pushing again may create a duplicate expense. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="repush-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRePush} data-testid="repush-confirm-btn">Push again</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
